/**
 * Shared city / region / country picker logic used by WhereDestinationPicker
 * (create trip modal) and onboarding destination sheets.
 */

import type {
  CitySearchResult,
  DestinationAutocompleteResult,
  PrimaryDestination,
} from '@/types/city';
import { ensureCompleteIdentifyingName } from '@/utils/cityMetadataBuilder';
import { getFirstPhotoUrl } from '@/utils/photoUtils';

export function isCitySearchResult(
  result: CitySearchResult | DestinationAutocompleteResult,
): result is CitySearchResult {
  return '_id' in result;
}

export function getCityResultPhotoUrl(result: CitySearchResult, size = 80): string | undefined {
  return (
    getFirstPhotoUrl(
      { photos: result.photos as Array<{ name?: string; photo_reference?: string }> | undefined },
      size,
    ) || undefined
  );
}

export function getCityResultDisplayLabels(result: CitySearchResult): {
  label: string;
  subLabel: string;
} {
  const label = result.city || result.name || result.label || '';
  const subLabel = result.city_identifying_name || '';
  return { label, subLabel };
}

export function getAutocompleteResultDisplayLabels(result: DestinationAutocompleteResult): {
  label: string;
  subLabel: string;
} {
  const label = result.display_name ?? result.identifying_name ?? '';
  const subLabel =
    result.identifying_name !== label ? result.identifying_name : '';
  return { label, subLabel };
}

/** Build PrimaryDestination for a city row — matches WhereDestinationPicker handleSelect. */
export function buildPrimaryDestinationFromCityResult(
  result: CitySearchResult,
): PrimaryDestination {
  const cityName = result.city || result.name || result.label || '';
  const rawIdentifying =
    result.city_identifying_name ||
    result.city ||
    result.name ||
    result.label ||
    '';
  const identifyingName = ensureCompleteIdentifyingName(cityName, rawIdentifying);

  return {
    type: 'city',
    identifying_name: identifyingName,
    ...(result.google_place_id ? { google_place_id: result.google_place_id } : {}),
  };
}

/** Build PrimaryDestination for region/country autocomplete — matches WhereDestinationPicker. */
export function buildPrimaryDestinationFromAutocompleteResult(
  result: DestinationAutocompleteResult,
): PrimaryDestination {
  return {
    type: result.type,
    identifying_name: result.identifying_name,
    ...(result.google_place_id ? { google_place_id: result.google_place_id } : {}),
  };
}

/** Whether a search row matches the current PrimaryDestination value. */
export function isDestinationSelected(
  value: PrimaryDestination | null,
  result: CitySearchResult | DestinationAutocompleteResult,
): boolean {
  if (!value) return false;

  if (isCitySearchResult(result)) {
    if (value.google_place_id && result.google_place_id) {
      return value.google_place_id === result.google_place_id;
    }
    const built = buildPrimaryDestinationFromCityResult(result);
    return value.type === 'city' && value.identifying_name === built.identifying_name;
  }

  if (value.google_place_id && result.google_place_id) {
    return value.google_place_id === result.google_place_id;
  }
  return value.type === result.type && value.identifying_name === result.identifying_name;
}
